import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import { motion } from 'framer-motion';

const ChiSono = () => {
  const { language } = useLanguage();

  return (
    <div data-testid="chi-sono-page" className="pt-32">
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Fotografo' : 'Photographer'}
          </p>
          <h1
            className="text-5xl sm:text-6xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="page-title"
          >
            {t('about.title', language)}
          </h1>
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            {t('about.subtitle', language)}
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <motion.img
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/eqpqqwpz_WhatsApp%20Image%202025-08-08%20at%2016.50.25.jpeg"
            alt="Matteo Giacometti"
            className="w-full h-auto object-cover rounded-sm"
          />
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'La Mia Storia' : 'My Story'}
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground mb-6">
              {t('about.story', language)}
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              {language === 'it'
                ? 'Ogni progetto è per me un\'opportunità di connettermi con le persone, di ascoltare le loro storie e di tradurle in immagini che parlano al cuore. Lavoro con passione, dedizione e un profondo rispetto per ogni momento che mi viene affidato.'
                : 'Every project is an opportunity for me to connect with people, to listen to their stories and to translate them into images that speak to the heart. I work with passion, dedication and deep respect for every moment entrusted to me.'}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Il Mio Approccio' : 'My Approach'}
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground mb-4">
              {language === 'it'
                ? 'Ogni persona, ogni famiglia e ogni storia meritano di essere raccontate con autenticità.'
                : 'Every person, every family and every story deserves to be told with authenticity.'}
            </p>
            <p className="text-base leading-relaxed text-muted-foreground mb-4">
              {language === 'it'
                ? 'Per questo il mio lavoro non parte dalla macchina fotografica, ma dall\'ascolto. Mi prendo il tempo per capire chi ho davanti, creare sintonia e lasciare che le emozioni emergano in modo naturale.'
                : 'That\'s why my work doesn\'t start with the camera, but with listening. I take the time to understand who I\'m dealing with, create harmony and let emotions emerge naturally.'}
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              {language === 'it'
                ? 'Non cerco immagini perfette. Cerco immagini vere: fotografie che vi rappresentino oggi e che, tra molti anni, abbiano ancora il potere di farvi rivivere ciò che avete provato.'
                : 'I don\'t look for perfect images. I look for real images: photographs that represent you today and that, many years from now, still have the power to make you relive what you felt.'}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full flex justify-center items-center"
          >
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/g0ljt6y7_IMG_5234.webp"
              alt="Matteo Giacometti al lavoro"
              className="h-auto object-cover rounded-sm"
              style={{ transform: 'rotate(90deg)', maxWidth: '100%' }}
            />
          </motion.div>
        </div>
      </section>

      <section className="bg-secondary/30 py-24 mt-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Perché Scegliere Me?' : 'Why Choose Me?'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div>
                <h3 className="text-xl font-medium mb-2">
                  {language === 'it' ? 'Autenticità' : 'Authenticity'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'it'
                    ? 'Niente pose forzate, solo momenti veri'
                    : 'No forced poses, only real moments'}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-2">
                  {language === 'it' ? 'Empatia' : 'Empathy'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'it'
                    ? 'Vi accompagno con sensibilità e discrezione'
                    : 'I accompany you with sensitivity and discretion'}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-2">
                  {language === 'it' ? 'Qualità' : 'Quality'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'it'
                    ? 'Immagini eleganti che durano nel tempo'
                    : 'Elegant images that last over time'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ChiSono;
